import Button, {ButtonVariant} from 'bitmovin-web-ui/src/components/core/button';
import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';

import AccordionPanel from '../../components/accordionPanel';
import BoundImageModal from '../../components/boundImageModal';
import {Link, pxToEm} from '../../components/globalStyles';
import WrapperAppUl from '../../components/wrapperAppUl';
import HardcodedSalesDemoPath from '../../data/hardcodedSalesDemoPath';
import {AppRoutePathFunction} from '../../logic/routes';
import {RootState} from '../../logic/store';
import {LoadingState, State as SalesDemosState} from '../../logic/store/salesDemosStore';
import ApiVectorGraphic2Svg from '../../media/cloudEncodingPageView/api_vector_graphic_2.svg';
import EncodingHybridPng from '../../media/onPremiseEncodingPageView/encoding_hybrid.png';

//#region Styled Components

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

const StyledAccordionPanel = styled(AccordionPanel)`
  width: 100%;

  :not(:last-child) {
    margin-bottom: ${pxToEm(18)}em;
  }
`;

const StyledApiVectorGraphicImgWrapper = styled.div`
  text-align: center;
  margin-top: 1em;
  > img {
    height: ${pxToEm(300)}em;
  }
`;

const StyledUlHeadlineSpan = styled.span`
  display: block;
  font-size: ${pxToEm(30)}em;
  color: ${baseVariables.colors.midnight};
  margin: 0.5em 0;
`;

const StyledButton = styled(Button)`
  margin-top: 1em;
`;

//#endregion

enum AccordionHeadline {
  HybritWorkflows = 'Hybrid Workflows',
  Drm = 'DRM',
  LiveStreaming = 'Live Streaming',
  ApiClients = 'API Clients',
  SupportedStorage = 'Supported Storage',
  InputFormats = 'Input Formats',
  OutputFormats = 'Output Formats',
  ClosedCaptionsSubtitles = 'Closed Captions & Subtitles',
  FullyFeaturedEncodingService = 'Fully Featured Encoding Service'
}

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends MapStateToProps {
  className?: string;
}

interface State {
  expandedAccordionHeadlines: AccordionHeadline;
}

class OnPremiseEncodingAccordions extends React.Component<Props, State> {
  public state: Readonly<State> = {
    expandedAccordionHeadlines: undefined
  };

  public componentDidMount(): void {
    this.setState({expandedAccordionHeadlines: undefined});
  }

  private handleAccordionPanelExpand = (accordionHeadline: AccordionHeadline) => {
    const isAlreadyExpanded = this.state.expandedAccordionHeadlines === accordionHeadline;
    this.setState({
      expandedAccordionHeadlines: isAlreadyExpanded ? undefined : accordionHeadline
    });
  };

  public render() {
    return (
      <StyledWrapperDiv className={this.props.className}>
        <StyledAccordionPanel
          headline={AccordionHeadline.HybritWorkflows}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.HybritWorkflows}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.HybritWorkflows)}>
          <WrapperAppUl>
            <li>Multi-Cloud and Cloud/On-Premise deployments</li>
            <li>Transparently controlled by a single API</li>
            <li>Easy handling of utilization peaks by scaling resources as required</li>
            <li>Utilize hardware for other/business related workloads at low encoding loads</li>
          </WrapperAppUl>

          <BoundImageModal
            boundComponent={<StyledButton variant={ButtonVariant.OutlinedLight}>Hybrid Encoding Workflow</StyledButton>}
            source={EncodingHybridPng}
          />
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.Drm}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.Drm}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.Drm)}>
          <WrapperAppUl>
            <li>Hollywood-/Netflix-grade DRM</li>
            <li>Widevine, PlayReady and Marlin with MPEG-CENC</li>
            <li>FairPlay DRM with HLS</li>
            <li>DASH ClearKey encryption</li>
            <li>SAMPLE-AES and AES-128 encryption for HLS</li>
          </WrapperAppUl>

          {this.renderDemoLinkButton(HardcodedSalesDemoPath.Drm)}
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.LiveStreaming}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.LiveStreaming}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.LiveStreaming)}>
          <WrapperAppUl>
            <li>Codecs: H264/AVC, H265/HEVC, VP9, AV1, AAC, AC3, E-AC3, MP3, Vorbis</li>
            <li>Support for 4K and 60 FPS</li>
            <li>Works in any cloud (e.g., AWS, Google, Azure) and On-Premise</li>
          </WrapperAppUl>

          {this.renderDemoLinkButton(HardcodedSalesDemoPath.LiveToVod)}
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.ApiClients}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.ApiClients}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.ApiClients)}>
          <WrapperAppUl>
            <li>Actively maintained API clients for all major languages</li>
            <li>Open-source API clients available on GitHub</li>
            <li>Fast and easy integration and setup</li>
          </WrapperAppUl>

          <StyledApiVectorGraphicImgWrapper>
            <img src={ApiVectorGraphic2Svg} alt="Api Vector Graphic" />
          </StyledApiVectorGraphicImgWrapper>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.SupportedStorage}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.SupportedStorage}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.SupportedStorage)}>
          <WrapperAppUl>
            <li>AWS S3</li>
            <li>Google Cloud Storage</li>
            <li>Azure Blob</li>
            <li>FTP</li>
            <li>SFTP</li>
            <li>HTTP/HTTPs</li>
            <li>Aspera</li>
            <li>Scality</li>
            <li>Cloudian HyperStore</li>
            <li>IBM Bluemix Storage</li>
            <li>IBM CleverSafe</li>
            <li>Rackspace Cloud Files</li>
            <li>Openstack Swift</li>
            <li>Apache CloudStack</li>
            <li>Ceph</li>
            <li>Riak</li>
            <li>DELL EMC</li>
            <li>HGST</li>
            <li>HP Helion Eucalyptus</li>
            <li>Oracle Nimbula</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.InputFormats}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.InputFormats}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.InputFormats)}>
          <StyledUlHeadlineSpan>Video Codecs</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>MPEG-1 Video</li>
            <li>MPEG-2 Video</li>
            <li>MPEG-4 Video</li>
            <li>H.261</li>
            <li>H.262</li>
            <li>H.263</li>
            <li>H.264/AVC</li>
            <li>H.265/HEVC</li>
            <li>VP6</li>
            <li>VP8</li>
            <li>VP9</li>
            <li>DNxHD</li>
            <li>Theora</li>
            <li>XDCAM</li>
            <li>XDCAM HD422</li>
            <li>XDCAM IMX</li>
            <li>DV</li>
            <li>DVCPRO</li>
            <li>DVCPROHD</li>
            <li>ProRes</li>
            <li>AVCHD</li>
            <li>AVCIntra</li>
            <li>Cineform HD</li>
            <li>Intel Indeo</li>
            <li>JPEG2000 (J2K)</li>
            <li>WMV</li>
            <li>Dolby Vision</li>
          </WrapperAppUl>

          <StyledUlHeadlineSpan>File Formats</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>MP4</li>
            <li>MKV</li>
            <li>MOV</li>
            <li>AVI</li>
            <li>FLV</li>
            <li>MPEG2 TS</li>
            <li>MPEG2 PS</li>
            <li>MXF</li>
            <li>LXF</li>
            <li>GXF</li>
            <li>3GP</li>
            <li>WebM</li>
            <li>MPG</li>
            <li>QuickTime</li>
          </WrapperAppUl>

          <StyledUlHeadlineSpan>Audio Codecs</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>AAC</li>
            <li>MP3</li>
            <li>AC3 / Dolby Digital</li>
            <li>E-AC3 / Dolby Digital Plus</li>
            <li>FLAC</li>
            <li>Vorbis</li>
            <li>PCM</li>
            <li>WAV</li>
            <li>Dolby Atmos</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.OutputFormats}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.OutputFormats}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.OutputFormats)}>
          <StyledUlHeadlineSpan>DRM</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>
              <a
                href="https://bitmovin.com/docs/player/tutorials/setup-the-bitmovin-player-with-multiple-drm-solutions"
                target="_blank">
                PlayReady
              </a>
            </li>
            <li>
              <a
                href="https://bitmovin.com/docs/player/tutorials/setup-the-bitmovin-player-with-multiple-drm-solutions"
                target="_blank">
                Widevine
              </a>
            </li>
            <li>
              <a
                href="https://bitmovin.com/docs/player/tutorials/setup-the-bitmovin-player-with-multiple-drm-solutions"
                target="_blank">
                Fairplay
              </a>
            </li>
            <li>
              <a 
                href={'https://bitmovin.com/docs/encoding/tutorials/' +
                'how-to-create-multi-drm-protected-content-with-intertrust-expressplay/'}
                target="_blank">
                Marlin
              </a>
            </li>
          </WrapperAppUl>

          <StyledUlHeadlineSpan>Streaming Protocols</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>MPEG-DASH</li>
            <li>Apple HLS</li>
            <li>Smooth Streaming</li>
          </WrapperAppUl>

          <StyledUlHeadlineSpan>Audio Codecs</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>AAC</li>
            <li>HE-AAC v1</li>
            <li>HE-AAC v2</li>
            <li>Dolby Digital</li>
            <li>Dolby Digital Plus</li>
            <li>Dolby Atmos</li>
            <li>MP2</li>
            <li>MP3</li>
            <li>Vorbis</li>
            <li>Opus</li>
            <li>DTS:HD and DTS:X</li>
          </WrapperAppUl>

          <StyledUlHeadlineSpan>Video Codecs</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>H.262/MPEG-2 Video (XDCAM HD 422)</li>
            <li>H.264/AVC</li>
            <li>H.265/HEVC (SDR and HDR)</li>
            <li>VP8</li>
            <li>VP9</li>
            <li>AV1</li>
            <li>MJPEG</li>
            <li>Dolby Vision</li>
          </WrapperAppUl>

          <StyledUlHeadlineSpan>Container Formats</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>Progressive MP4</li>
            <li>Fragmented MP4</li>
            <li>MOV</li>
            <li>MPEG2-TS</li>
            <li>WebM</li>
            <li>CMAF</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.ClosedCaptionsSubtitles}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.ClosedCaptionsSubtitles}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.ClosedCaptionsSubtitles)}>
          <WrapperAppUl>
            <li>WebVTT</li>
            <li>CEA-608/708</li>
            <li>SRT</li>
            <li>TTML</li>
            <li>DFXP</li>
            <li>EBU-TT</li>
            <li>SMPTE-TT</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.FullyFeaturedEncodingService}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.FullyFeaturedEncodingService}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.FullyFeaturedEncodingService)}>
          <WrapperAppUl>
            <li>Encoding speed of up to 100x real-time</li>
            <li>MPEG-DASH, HLS, Smooth Streaming</li>
            <li>Encoding & packaging</li>
            <li>Highly customizable encoder</li>
            <li>Fast and easy integration and setup</li>
            <li>Zero downtime deployments and upgrades</li>
            <li>Actively maintained API clients for all major languages</li>
            <li>Comprehensive, actively maintained documentation</li>
            <li>
              Inputs & Outputs: AWS S3, GCS, Azure, FTP, SFTP, HTTP, Aspera, Scality, Swift, Rackspace, Cloudian, IBM
              Bluemix, S3 compatible protocols
            </li>
            <li>World class support</li>
            <li>Containers: MP4, MPEG-TS, WebM, CFF, MOV</li>
            <li>Live HEVC, VP9, AV1, DRM, 60FPS, 4K</li>
            <li>Live to VoD</li>
            <li>MPEG-CENC DRM & Offline DRM support</li>
            <li>Widevine, PlayReady, Marlin and Fairplay</li>
            <li>Multi-Cloud (AWS, Google, Azure)</li>
            <li>Codecs: H264/AVC, H265/HEVC, VP9, AV1, AAC, AC3, E-AC3, MP3, Vorbis</li>
            <li>Subtitles and Closed Captions: WebVTT, SRT, TTML, SMPTE-TT, EBU-TT, DFXP, 608/708, SCC</li>
            <li>Thumbnails</li>
            <li>Sprites</li>
            <li>Watermarks</li>
            <li>Logo Insertion</li>
            <li>Cropping</li>
            <li>Rotate</li>
            <li>Deinterlace</li>
            <li>Sidecar File Passthrough</li>
            <li>Audio Channel Mixing</li>
          </WrapperAppUl>
        </StyledAccordionPanel>
      </StyledWrapperDiv>
    );
  }

  public renderDemoLinkButton = (demoPath: string) => {
    const {loadingState, response} = this.props.salesDemos;

    if (loadingState !== LoadingState.Success) {
      return null;
    }

    const allLoadedDemos = response.demos || [];
    const foundDemo = allLoadedDemos.find(salesDemo => salesDemo.path === demoPath);
    if (foundDemo === undefined) {
      return null;
    }

    return (
      <Link to={AppRoutePathFunction.DemoDetail({demoId: foundDemo.id})}>
        <StyledButton variant={ButtonVariant.OutlinedLight}>{foundDemo.name}&nbsp;Demo</StyledButton>
      </Link>
    );
  };
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(OnPremiseEncodingAccordions);
